import axios from "axios";
import { EdgeKind } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.service";
import { useEffect, useMemo, useState } from "react";
import { fetchExecutionRelationKinds } from "./executionRelations.service";

/**
 * Load the Children-tab "Relation" column kind map for exactly the current page's
 * rows, server-side, via `GET /execution/{root}/process/relations`.
 *
 * Keyed on the root execution id + the set of visible row ids, so it refetches
 * only when the page (or its rows) changes, not on every live-update tick that
 * leaves the id set unchanged. Fails soft: any error yields an empty map, so the
 * column renders "—" rather than breaking. Returns `undefined` when there is no
 * root execution (the plain executions list, where the column is omitted).
 */
export const useExecutionRelationKinds = (
    rootExecutionId: string | null | undefined,
    rowIds: string[]
): Record<string, EdgeKind> | undefined => {
    const [kindMap, setKindMap] = useState<Record<string, EdgeKind>>({});

    // A stable key for the visible row-id set. `rowIds` is a fresh array on every
    // render (and on every live-update tick), so depending on it directly would
    // refetch constantly; the joined string only changes when the ids do.
    const idsKey = useMemo(() => rowIds.join(","), [rowIds]);

    useEffect(() => {
        if (!rootExecutionId || !idsKey) {
            setKindMap({});
            return;
        }
        const controller = new AbortController();
        fetchExecutionRelationKinds(rootExecutionId, idsKey.split(","), {
            signal: controller.signal,
        })
            .then((kinds) => setKindMap(kinds))
            .catch((error) => {
                // Ignore aborts (superseded page/root) so we don't clear a map a
                // newer fetch is about to populate; any real error fails soft to "—".
                if (!axios.isCancel(error)) {
                    setKindMap({});
                }
            });
        return () => controller.abort();
    }, [rootExecutionId, idsKey]);

    return rootExecutionId ? kindMap : undefined;
};
